import type { MessageDescriptor } from "react-intl";
import { navMessages, userMenuMessages } from "../../access/messages.ts";
import { shellMessages } from "../messages.ts";

/** One crumb of the top bar trail; `to` is absent for the current page and for plain groups. */
export interface CrumbSpec {
  readonly id: string;
  /** A catalog message, or `text` for an identifier taken from the URL. */
  readonly message?: MessageDescriptor;
  readonly text?: string;
  /** Show `text` through `shortenId` (device and transaction identifiers). */
  readonly shorten?: boolean;
  readonly mono?: boolean;
  readonly to?: string;
  /** Search params of the link (the user list's `tab`, the selected organization's `org`). */
  readonly linkSearch?: Readonly<Record<string, string>>;
}

/** What the trail of a Platform operator detail needs beyond the URL (design D9). */
export interface PlatformTrailContext {
  /** The operator's organization; absent while unknown, or for an operator not adopted yet. */
  readonly organization?: { readonly id: string; readonly name: string } | undefined;
}

function decode(segment: string | undefined): string {
  if (!segment) return "";
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * The route breadcrumbs of the top bar (design D1 of `align-explorer-screens-with-design`):
 * `Players › <player_ref>`, `Devices › <short id>`, `Transactions › <short id>`,
 * `Platform › Organizations › <organization> › <operator>` (design D9 of
 * reorganize-platform-area-by-organization), and a group › page trail for the other screens. The home screen
 * shows `Search`, followed by the searched identifier when there is one (`Search › 189.40.12.7`, design
 * QEIkI). `tab` is the user list's tab (`users` or `devices`), `query` the home search (`q`).
 */
export function breadcrumbSpecs(
  pathname: string,
  tab?: string,
  query?: string,
  platform: PlatformTrailContext = {},
): readonly CrumbSpec[] {
  const [first, second, third] = pathname.split("/").filter(Boolean);
  switch (first) {
    case undefined:
      return query
        ? [
            { id: "home", message: shellMessages.navSearch, to: "/" },
            { id: "query", text: query, mono: true },
          ]
        : [{ id: "home", message: shellMessages.navSearch }];
    case "users":
      return second
        ? [
            { id: "players", message: shellMessages.crumbPlayers, to: "/users" },
            { id: "player", text: decode(second), mono: true },
          ]
        : [
            { id: "list", message: shellMessages.navUsers },
            tab === "devices"
              ? { id: "devices", message: shellMessages.crumbDevices }
              : { id: "players", message: shellMessages.crumbPlayers },
          ];
    case "devices":
      return [
        {
          id: "devices",
          message: shellMessages.crumbDevices,
          to: "/users",
          linkSearch: { tab: "devices" },
        },
        { id: "device", text: decode(second), shorten: true, mono: true },
      ];
    case "transactions":
      return [
        { id: "transactions", message: shellMessages.crumbTransactions, to: "/users" },
        { id: "transaction", text: decode(second), shorten: true, mono: true },
      ];
    case "ips":
      return [
        { id: "transactions", message: shellMessages.crumbTransactions, to: "/users" },
        { id: "ip", text: decode(second), mono: true },
      ];
    case "blocklist":
      return [{ id: "blocklist", message: shellMessages.navBlocklist }];
    case "map":
      return [{ id: "map", message: shellMessages.navMap }];
    case "settings":
      return [{ id: "settings", message: userMenuMessages.settings }];
    case "console-users":
      return [
        { id: "administer", message: shellMessages.crumbAdminister },
        { id: "console-users", message: navMessages.consoleUsers },
      ];
    case "identity-provider":
      return [
        { id: "administer", message: shellMessages.crumbAdminister },
        { id: "identity-provider", message: navMessages.identityProvider },
      ];
    case "audit":
      return [
        { id: "administer", message: shellMessages.crumbAdminister },
        { id: "audit", message: navMessages.audit },
      ];
    case "platform": {
      const platformRoot: CrumbSpec = { id: "platform", message: shellMessages.crumbPlatform };
      if (second === "organizations") {
        return [
          platformRoot,
          { id: "organizations", message: shellMessages.navPlatformOrganizations },
        ];
      }
      if (second === "fleet") {
        return [platformRoot, { id: "fleet", message: shellMessages.navPlatformFleet }];
      }
      if (second === "administrators") {
        return [
          platformRoot,
          { id: "administrators", message: shellMessages.navPlatformAdministrators },
        ];
      }
      if (third) {
        // The operator detail: through its organization (its record opens with it selected).
        const organization = platform.organization;
        return [
          platformRoot,
          {
            id: "organizations",
            message: shellMessages.navPlatformOrganizations,
            to: "/platform/organizations",
          },
          ...(organization
            ? [
                {
                  id: "organization",
                  text: organization.name,
                  to: "/platform/organizations",
                  linkSearch: { org: organization.id },
                },
              ]
            : []),
          { id: "operator", text: decode(third) },
        ];
      }
      return [platformRoot, { id: "operators", message: shellMessages.navPlatformOperators }];
    }
    default:
      return [{ id: "home", message: shellMessages.home, to: "/" }];
  }
}

/** The id segment of `/platform/operators/<id>`, when the route is an operator detail. */
export function platformOperatorIdOf(pathname: string): string | undefined {
  const [first, second, third] = pathname.split("/").filter(Boolean);
  return first === "platform" && second === "operators" && third ? decode(third) : undefined;
}
